/**
 * Feedback System Module
 * Handles university feedback (correct/incorrect) functionality
 */

export const feedbackSystem = {
    // Initialize feedback system
    // DEPRECATED: Feedback is now handled by Livewire component
    // This method is kept for backward compatibility but does nothing
    initializeFeedbackSystem() {
        // Feedback is now handled by Livewire - no JavaScript initialization needed
        return;
    },

    // Setup feedback event listeners
    setupFeedbackEventListeners() {
        // Use event delegation to prevent duplicate event listeners
        if (!document.body.dataset.universityFeedbackInitialized) {
            document.body.dataset.universityFeedbackInitialized = 'true';

            document.addEventListener('click', async (e) => {
                if (e.target && e.target.id === 'uni_info_correct') {
                    e.preventDefault();
                    await this.submitUniversityFeedback(e.target.dataset.uniId, 'correct', e.target);
                } else if (e.target && e.target.id === 'uni_info_incorrect') {
                    e.preventDefault();
                    await this.submitUniversityFeedback(e.target.dataset.uniId, 'incorrect', e.target);
                }
            });
        }
    },

    // Submit university feedback
    async submitUniversityFeedback(universityId, type, button) {
        // Prevent multiple executions
        if (button._isSubmitting) {
            return;
        }

        // Set submitting flag early to prevent multiple executions
        button._isSubmitting = true;

        const originalContent = button.innerHTML;
        button.innerHTML = '<i class="fas fa-spinner fa-spin ml-1.5"></i>در حال ارسال...';
        button.disabled = true;

        // Disable both buttons during submission
        const correctBtn = document.getElementById('uni_info_correct');
        const incorrectBtn = document.getElementById('uni_info_incorrect');
        if (correctBtn) correctBtn.disabled = true;
        if (incorrectBtn) incorrectBtn.disabled = true;

        // Convert type to expected format
        const feedback = type === 'correct' ? 'like' : 'dislike';

        try {
            // Use the correct route and HTTP method
            const response = await fetch(`/university/${universityId}/feedback/ajax`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': this.getCsrfToken()
                },
                body: JSON.stringify({
                    feedback: feedback
                })
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error('Unauthorized');
                }
                throw new Error('Network response was not ok');
            }

            const data = await response.json();

            if (data.status === 200) {
                this.showMessage('success', data.message);
                this.handleFeedbackSuccess(button, feedback);
            } else {
                // Re-enable buttons on error
                this.reEnableFeedbackButtons(correctBtn, incorrectBtn, button, originalContent);
                this.showMessage('error', data.message || 'خطایی رخ داد');
            }
        } catch (error) {
            console.error('Feedback error:', error);
            // Re-enable buttons on error
            this.reEnableFeedbackButtons(correctBtn, incorrectBtn, button, originalContent);

            if (error.message === 'Unauthorized') {
                this.showMessage('error', 'لطفا وارد حساب کاربری خود شوید');
                setTimeout(() => {
                    window.location.href = '/login';
                }, 1500);
            } else {
                this.showMessage('error', 'خطا در اتصال به سرور');
            }
        } finally {
            button._isSubmitting = false; // Reset flag
        }
    },

    // Re-enable feedback buttons
    reEnableFeedbackButtons(correctBtn, incorrectBtn, button, originalContent) {
        if (correctBtn) correctBtn.disabled = false;
        if (incorrectBtn) incorrectBtn.disabled = false;
        button.innerHTML = originalContent;
    },

    // Handle feedback success
    handleFeedbackSuccess(button, feedback) {
        // Find the feedback section (the div containing the buttons)
        const feedbackSection = button.closest('.feedback-section');
        if (feedbackSection) {
            // Hide the feedback section with smooth animation
            feedbackSection.style.opacity = '0';
            feedbackSection.style.transform = 'translateY(-10px)';
            setTimeout(() => {
                feedbackSection.style.display = 'none';

                // Show success message
                this.showSuccessMessage(feedbackSection, feedback);
            }, 300);
        }
    },

    // Show success message
    showSuccessMessage(feedbackSection, feedback) {
        const successMessage = document.createElement('div');
        successMessage.className = 'mt-4 p-4 bg-green-50 border border-green-200 rounded-lg';
        successMessage.innerHTML = `
            <div class="flex items-center">
                <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mr-3">
                    <i class="fas fa-check text-green-600 text-sm"></i>
                </div>
                <div>
                    <p class="text-green-800 text-sm font-medium">نظر شما با موفقیت ثبت شد</p>
                    <p class="text-green-700 text-xs">از مشارکت شما در بهبود کیفیت اطلاعات سپاسگزاریم</p>
                </div>
            </div>
        `;
        feedbackSection.parentNode.appendChild(successMessage);

        // If it's a dislike, show the correction modal option
        if (feedback === 'dislike') {
            setTimeout(() => {
                this.showCorrectionOption(successMessage);
            }, 1000);
        }
    },

    // Show correction option
    showCorrectionOption(successMessage) {
        // Get university ID from various sources
        const universityId = this.universityId || (() => {
            // Try to get from Alpine component
            const alpineElement = document.querySelector('[x-data*="universityShow"]');
            if (alpineElement && alpineElement.__x) {
                return alpineElement.__x.$data?.universityId || alpineElement.__x.$data?.config?.universityId;
            }
            // Try to extract from URL
            const urlMatch = window.location.pathname.match(/\/universities\/(\d+)/);
            if (urlMatch) {
                return parseInt(urlMatch[1]);
            }
            return null;
        })();

        const correctionOption = document.createElement('div');
        correctionOption.className = 'mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg';
        correctionOption.innerHTML = `
            <div class="flex items-center justify-between">
                <div class="flex items-center">
                    <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <i class="fas fa-edit text-blue-600 text-sm"></i>
                    </div>
                    <div>
                        <p class="text-blue-800 text-sm font-medium">آیا می‌خواهید اطلاعات نادرست را گزارش دهید؟</p>
                        <p class="text-blue-700 text-xs">با گزارش تغییرات، به بهبود دقت اطلاعات کمک می‌کنید</p>
                    </div>
                </div>
                <button onclick="handleUniversityCorrectionAction(${universityId || 'null'})"
                        class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center">
                    <i class="fas fa-edit ml-1.5"></i>
                    گزارش تغییرات
                </button>
            </div>
        `;
        successMessage.parentNode.appendChild(correctionOption);
    },

    // Open correction modal using Livewire
    openCorrectionModal() {
        // Get university ID from Alpine component or page data
        const universityId = window.universityId || (() => {
            const component = document.querySelector('[x-data*="universityShow"]')?.__x?.$data;
            return component?.universityId || null;
        })();

        if (!universityId) {
            console.error('University ID not found. Cannot open correction modal.');
            return;
        }

        // Open Alpine.js modal instead of Livewire
        const modal = document.getElementById('university-correction-modal');
        if (modal) {
            modal.dispatchEvent(new CustomEvent('open-modal'));
        } else {
            console.warn('University correction modal not found');
        }
    }
};
